import { Component, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { SimpleUser } from 'app/models';
import { combineLatest, map, Observable, of, Subscription } from 'rxjs';
import { ScreenService } from 'app/services/screen.service';
import { DisplayMode } from '../../models/screen.model';
import { StripTagsPipe } from '../../../../shared/pipes/strip-tags.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { AvatarComponent } from '../avatar/avatar.component';
import { AsyncPipe } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FollowState } from 'app/store/me/me.state';
import { FollowApi } from '../../api/follow.api';
import { PaginableUsersHttpResult } from '../../models/users.model';
import { VarDirective } from 'app/shared/directives/ng-var.directive';
import { MeActions } from 'app/store/me/me.actions';
import {
  selectFollowersLoaded,
  selectFollowersLoading,
  selectFollowingsLoaded,
  selectFollowingsLoading,
  selectFollows,
} from 'app/store/me/me.selectors';
import { SkeletonComponent } from '@kinomap-dev/ng-ui-lib';

export type FollowType = 'FOLLOWER' | 'FOLLOWING';

@Component({
  selector: 'app-list-follow',
  templateUrl: './list-follow.component.html',
  styleUrls: ['./list-follow.component.scss'],
  standalone: true,
  imports: [
    InfiniteScrollDirective,
    AvatarComponent,
    RouterLink,
    SkeletonComponent,
    AsyncPipe,
    TranslateModule,
    StripTagsPipe,
    VarDirective,
  ],
})
export class ListFollowComponent implements OnDestroy {
  @Input() scrollContainerSelector: string;
  @Input() userId: number | string;
  @Input() followType: FollowType;
  @Input() totalItems: number;

  inputChecked: number = 0;
  follows$: Observable<FollowState>;
  users$: Observable<SimpleUser[]> = of([]);
  usersLoading$: Observable<boolean> = of<boolean>(false);
  usersLoaded$: Observable<boolean> = of<boolean>(false);
  displayModeEnum: typeof DisplayMode = DisplayMode;
  page: number = null;
  subscriptions: Subscription[] = [];

  constructor(
    public readonly screen: ScreenService,
    private readonly store: Store,
    private followApi: FollowApi
  ) {}

  getFollows(isLoaded: boolean = false): void {
    this.inputChecked = 0;

    if (isLoaded && !this.userId) {
      return;
    }

    if (this.userId === 'me') {
      this.follows$ = this.store.select(selectFollows);

      return this.getMyFollows();
    }

    this.getUserFollows();
  }

  getMyFollows(): void {
    if (typeof this.userId !== 'string') {
      return console.error('getMyFollows(): userId is not "me"');
    }

    if (this.followType === 'FOLLOWER') {
      this.usersLoaded$ = this.store.select(selectFollowersLoaded);
      this.usersLoading$ = this.store.select(selectFollowersLoading);
      this.users$ = this.follows$.pipe(
        map((follows: FollowState) => follows.followers.users.data)
      );
      return this.store.dispatch(MeActions.getFollowers({}));
    }

    this.usersLoaded$ = this.store.select(selectFollowingsLoaded);
    this.usersLoading$ = this.store.select(selectFollowingsLoading);

    this.users$ = this.follows$.pipe(
      map((follows: FollowState) => follows.followings.users.data)
    );
    this.store.dispatch(MeActions.getFollowings({}));
  }

  getUserFollows(): number {
    if (typeof this.userId !== 'number') {
      console.error('getUserFollows(): userId is not a number');
      return;
    }

    this.usersLoading$ = of(true);
    this.page = this.page ? this.page + 1 : 1;


    if (this.followType === 'FOLLOWER') {
      return this.subscriptions.push(
        this.followApi
          .getFollowers(this.page, 15, this.userId)
          .subscribe((followers: PaginableUsersHttpResult) => {
            this.setUserFollows(followers);
          })
      );
    }

    this.subscriptions.push(
      this.followApi
        .getFollowings(this.page, 15, this.userId)
        .subscribe((followings: PaginableUsersHttpResult) =>
          this.setUserFollows(followings)
        )
    );
  }

  setUserFollows(follows: PaginableUsersHttpResult) {
    this.users$ = combineLatest([this.users$, of(follows.data)]).pipe(
      map(([users, follows]: SimpleUser[][]) => [...users, ...follows])
    );
    this.usersLoading$ = of(false);
    if (follows.meta.current_page === follows.meta.last_page) {
      this.usersLoaded$ = of(true);
    }
  }

  reset(): void {
    this.usersLoading$ = of(true);
    this.usersLoaded$ = of(false);
    this.userId = null;
    this.userId = null;
    this.users$ = of([]);
    this.page = null;
  }

  ngOnDestroy(): void {
    this.reset();
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  ngOnInit(): void {
    this.getFollows();
  }
}
